/**
 * One-tap trading: a preset amount of money, or a share of a position, turned
 * into the one order a tap places — in integers (invariant 6).
 *
 * The ticket's `1-Tap` type (`docs/07` §1c) shows three buttons. On the buy
 * side each is a dollar amount ($5 / $25 / $100 by default, editable per
 * device); on the sell side each is a share of what the account can sell
 * (25% / 50% / All). A tap places the order at once, with no confirm step, so
 * everything a user is told before the tap — what it wins, what it returns —
 * has to be the order that is then signed. These functions are that order.
 *
 * ## A tap never spends more than its amount
 *
 * The venue sizes orders in shares, never in dollars (`docs/04` §7.1). So a
 * buy preset walks the book with the amount as its budget — `quoteOrder`'s
 * budgeted walk, the one the ticket's slider uses for its 100% — and is signed
 * as a **limit at the deepest price that walk reached, `IOC`**: it takes what
 * is there now and nothing is left resting. And it is sized at that one price,
 * not across the walk, because the venue holds `size × limit` for a limit order
 * — sized across the walk, a tap spanning two levels would ask the venue to
 * hold more than the amount. So `size × limit + fee ≤ amount`, always, and the
 * book moving first only means less fills.
 *
 * ## A sell never sells more than is held
 *
 * A sell preset is a share of the account's deliverable shares — the position
 * less what its own resting sells have promised (`coverableShares`). So it is
 * always wholly covered, and `domain/sell-shape.ts`'s rewrite of an uncovered
 * sell into a buy of the other side (invariant 21) never happens on this
 * surface: a tap has no "before" to explain it in. It is quoted as the buy of
 * the complement it is on the book (§7.8) and signed as a limit at the lowest
 * price that walk sells at, `IOC`.
 *
 * No React and no DOM (invariant 10).
 */

import type { Outcome } from '@/lib/decimal'
import type { BookState } from '@/stores/book'
import type { TradingLimits } from './order-config'
import { floorToStep } from './order-validation'
import {
  affordableAt,
  EMPTY_QUOTE,
  quoteOrder,
  reachableLevels,
  saleProceedsMicroUsdc,
  type Quote,
} from './quote'
import { complementOf, complementPriceCents } from './sell-shape'

/** The sell presets, as percentages of what the account can sell. */
export const ONE_TAP_SELL_PERCENTS = [25, 50, 100] as const

/** Why a preset places nothing — the cases `validateOrder` has no sentence for. */
export type OneTapEmpty = 'NO_LIQUIDITY' | 'NOTHING_HELD' | 'TOO_SMALL'

export interface OneTapOrder {
  side: 'BUY' | 'SELL'
  outcome: Outcome
  /** Micro-shares signed. `0n` when the preset places nothing — see `empty`. */
  sizeShares: bigint
  /** The signed limit, in the user's own outcome's terms. `null` when nothing is placed. */
  limitCents: number | null
  /**
   * The outcome the **book** is asked about: the buy's own, or the complement a
   * sell is quoted as (§7.8). `validateOrder` and the quote are in these terms.
   */
  quoteOutcome: Outcome
  /** `limitCents` in `quoteOutcome`'s terms. */
  quoteLimitCents: number | null
  quote: Quote
  /** The part of the order that closes a position: all of a sell, none of a buy. */
  coveredShares: bigint
  /** A buy: what it pays if it wins, shares × $1. A sell: what it returns. Micro-USDC. */
  valueMicroUsdc: bigint
  /** Why `sizeShares` is zero, or `null` when it is not. */
  empty: OneTapEmpty | null
}

/**
 * The buy a tap on a dollar preset places.
 *
 * Walks the book with `amountMicroUsdc` as the budget, then sizes the order at
 * the deepest price the walk reached — see the header for why that, and not
 * the walk's own size, is what keeps a tap inside its amount.
 */
export function oneTapBuy(
  book: BookState | null,
  outcome: Outcome,
  amountMicroUsdc: bigint,
  limits: TradingLimits,
): OneTapOrder {
  const rate = limits.takerFeeRate4dp
  const walk = quoteOrder(book, {
    outcome,
    side: 'BUY',
    sizeShares: limits.maxOrderShares,
    limitPriceCents: null,
    takerFeeRate4dp: rate,
    budgetMicroUsdc: amountMicroUsdc > 0n ? amountMicroUsdc : 0n,
    sizeStep: limits.sizeStep,
  })
  const deepest = walk.worstPriceCents
  if (deepest === null) {
    // Nothing reachable at all, or an amount that buys not one step at the touch.
    return nothing('BUY', outcome, outcome, reachableLevels(book, outcome).length > 0 ? 'TOO_SMALL' : 'NO_LIQUIDITY')
  }
  const atDeepest = affordableAt(amountMicroUsdc, deepest, rate, limits.sizeStep)
  const size = atDeepest < walk.filledShares ? atDeepest : walk.filledShares
  if (size <= 0n) return nothing('BUY', outcome, outcome, 'TOO_SMALL')
  const quote = quoteOrder(book, {
    outcome,
    side: 'BUY',
    sizeShares: size,
    limitPriceCents: deepest,
    takerFeeRate4dp: rate,
  })
  return {
    side: 'BUY',
    outcome,
    sizeShares: size,
    limitCents: deepest,
    quoteOutcome: outcome,
    quoteLimitCents: deepest,
    quote,
    coveredShares: 0n,
    valueMicroUsdc: quote.payoutMicroUsdc,
    empty: null,
  }
}

/**
 * The sell a tap on a percentage preset places: `percent` of what the account
 * can sell, floored to the step, and never more than the book will take now.
 */
export function oneTapSell(
  book: BookState | null,
  outcome: Outcome,
  deliverableShares: bigint,
  percent: number,
  limits: TradingLimits,
): OneTapOrder {
  const complement = complementOf(outcome)
  if (deliverableShares <= 0n) return nothing('SELL', outcome, complement, 'NOTHING_HELD')
  const share = percent >= 100 ? deliverableShares : (deliverableShares * BigInt(Math.floor(percent))) / 100n
  const wanted = floorToStep(share, limits.sizeStep)
  if (wanted <= 0n) return nothing('SELL', outcome, complement, 'TOO_SMALL')

  const walk = quoteOrder(book, {
    outcome: complement,
    side: 'BUY',
    sizeShares: wanted,
    limitPriceCents: null,
    takerFeeRate4dp: limits.takerFeeRate4dp,
  })
  const deepest = walk.worstPriceCents
  // The book may hold fewer shares than the preset asks for; a tap sells what
  // it will take now, which is what the button says it returns.
  const size = floorToStep(walk.filledShares, limits.sizeStep)
  if (deepest === null || size <= 0n) return nothing('SELL', outcome, complement, 'NO_LIQUIDITY')
  const quote = quoteOrder(book, {
    outcome: complement,
    side: 'BUY',
    sizeShares: size,
    limitPriceCents: deepest,
    takerFeeRate4dp: limits.takerFeeRate4dp,
  })
  return {
    side: 'SELL',
    outcome,
    sizeShares: size,
    limitCents: complementPriceCents(deepest),
    quoteOutcome: complement,
    quoteLimitCents: deepest,
    quote,
    coveredShares: size,
    valueMicroUsdc: saleProceedsMicroUsdc(size, quote.avgPriceCents4dp),
    empty: null,
  }
}

/**
 * The price a tap would trade at first, in the user's own terms — what the
 * Up/Down buttons show in `1-Tap` mode: the cheapest share on a buy, the best
 * price a share sells for on a sell. `null` on an empty side.
 *
 * Standard mode shows the last trade instead (`ticker.up_price`), which is not
 * what a tap pays and is why this exists.
 */
export function oneTapPrice(book: BookState | null, outcome: Outcome, side: 'BUY' | 'SELL'): number | null {
  if (side === 'BUY') return reachableLevels(book, outcome)[0]?.priceCents ?? null
  const touch = reachableLevels(book, complementOf(outcome))[0]?.priceCents
  return touch === undefined ? null : complementPriceCents(touch)
}

// ---------------------------------------------------------------------------
// The editable amounts
// ---------------------------------------------------------------------------

/** Whole dollars up to a million, and at most two decimal places. */
const AMOUNT_RE = /^(\d{1,7})(?:\.(\d{1,2}))?$/

/**
 * A preset amount as typed — `25`, `2.5`, `$25.00` — in micro-USDC, or `null`
 * when it is not a positive amount of dollars and cents.
 */
export function parseOneTapAmount(text: string): bigint | null {
  const match = AMOUNT_RE.exec(text.trim().replace(/^\$/, '').replace(/,/g, ''))
  if (!match?.[1]) return null
  const micro = BigInt(match[1]) * 1_000_000n + BigInt((match[2] ?? '').padEnd(2, '0')) * 10_000n
  return micro > 0n ? micro : null
}

/** The stored spelling of an amount: `25`, or `2.50` when it has cents. */
export function formatOneTapAmount(micro: bigint): string {
  const whole = micro / 1_000_000n
  const cents = (micro % 1_000_000n) / 10_000n
  return cents === 0n ? String(whole) : `${whole}.${String(cents).padStart(2, '0')}`
}

/**
 * The stored form of the three amounts — `5,25,100` — as a storage guard.
 *
 * Three, each one `parseOneTapAmount` accepts in its stored spelling. Anything
 * else (an older shape, a hand-edited key) falls back to the defaults rather
 * than being trusted through a cast (`app/stored.ts`).
 */
export function isOneTapAmounts(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const parts = value.split(',')
  return parts.length === 3 && parts.every((part) => AMOUNT_RE.test(part) && parseOneTapAmount(part) !== null)
}

function nothing(side: 'BUY' | 'SELL', outcome: Outcome, quoteOutcome: Outcome, empty: OneTapEmpty): OneTapOrder {
  return {
    side,
    outcome,
    sizeShares: 0n,
    limitCents: null,
    quoteOutcome,
    quoteLimitCents: null,
    quote: EMPTY_QUOTE,
    coveredShares: 0n,
    valueMicroUsdc: 0n,
    empty,
  }
}
